import { fetch } from "~/utils/helper";
import { CONTACT_EMAIL } from "~/composables/useHelpDesk";

// The account's plan and its state (GET /accounts/:id/subscription): « Solde et forfait », the cancellation path, the
// notice of a plan that ends and the welcome back of /welcome read the same state. `cancel` schedules the cancellation
// at the end of the period, `resume` withdraws it; both answer with the new state, which the screens then show.
//
// summary: { plan: { id, displayname, price } | null, billing: "stripe" | "manual" | "none", status, trialEndsAt,
//   renewsAt, endsAt, endedAt } (api services/accounts/subscriptionSummary.js).
export const useSubscription = () => {
  const accountId = useNuxtApp().$account?.id;
  const state = useState(`subscription:${ accountId }`, () => ({ summary: null, loading: false, failed: false }));
  const patch = (changes) => { state.value = { ...state.value, ...changes }; };

  const load = async () => {
    if (state.value.loading) return;
    patch({ loading: true });
    try {
      const res = await fetch("/accounts/:accountId/subscription");
      patch({ summary: res?.subscription ?? null, failed: !res?.success });
    } catch {
      patch({ failed: true });
    } finally {
      patch({ loading: false });
    }
  };

  // Resolves to "done", or to the API's reason for refusing: "manual" (a plan the team gave, which the team cancels),
  // "forbidden" (the account's owner or an administrator only), "failed".
  const change = async (method, body) => {
    try {
      const res = await fetch("/accounts/:accountId/subscription/cancellation", { method, ...(body ? { body } : {}) });
      if (!res?.success) return "failed";
      patch({ summary: res.subscription });
      return "done";
    } catch (error) {
      if (error?.code === "NO_STRIPE_SUBSCRIPTION") return "manual";
      if (error?.code === "ACCOUNT_MANAGER_REQUIRED") return "forbidden";
      return "failed";
    }
  };
  const cancel = ({ reason, comment }) => change("POST", { reason, comment });
  const resume = () => change("DELETE");

  // The cancellation path (components/billing/CancelPlanDialog.vue, mounted with the header): one window for the account.
  const cancelOpen = useState("cancel-plan-open", () => false);
  const openCancel = () => { cancelOpen.value = true; };

  // « Reprendre mon abonnement », from « Solde et forfait » or the notice of a plan that ends: the withdrawal, and a word
  // that says it worked, or why not.
  const toast = useToast();
  const { trackCancellation } = useTracking();
  const keepPlan = async () => {
    const { t } = useNuxtApp().$i18n;
    const outcome = await resume();
    if (outcome === "done") {
      trackCancellation({ step: "withdrawn" });
      toast.add({ id: "plan-resume", title: t("billing.resume.done"), description: t("billing.resume.doneText"), icon: "i-heroicons-check-circle", timeout: 5000 });
      return;
    }
    toast.add({
      id: "plan-resume",
      title: t("billing.resume.failed"),
      description: outcome === "forbidden" ? t("billing.resume.forbidden") : t("billing.resume.failedText", { email: CONTACT_EMAIL }),
      icon: "i-heroicons-x-circle",
      color: "error",
      timeout: 7000
    });
  };

  return {
    summary: computed(() => state.value.summary),
    loading: computed(() => state.value.loading),
    failed: computed(() => state.value.failed),
    load,
    cancel,
    resume,
    keepPlan,
    cancelOpen,
    openCancel
  };
};
